import { useState } from "react";
import { FileDown, Loader2, CheckCircle2 } from "lucide-react";
import Seo from "@/components/Seo";
import { PageHero, Reveal, TickList } from "@/components/ui-bits";
import { useSettings, useCategories } from "@/hooks/useContent";
import { api, postLead } from "@/lib/api";
import { track } from "@/lib/site";

export default function Catalogue() {
  const { data: settings } = useSettings();
  const { data: categories } = useCategories();
  const [form, setForm] = useState({ name: "", company: "", country: "", email: "" });
  const [busy, setBusy] = useState(false);
  const [unlocked, setUnlocked] = useState(!settings?.catalogue_requires_info);
  const [downloaded, setDownloaded] = useState(false);

  const catalogueUrl = settings?.catalogue_url || "";
  const needsInfo = settings?.catalogue_requires_info !== false;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await postLead({ type: "contact", ...form, product: "Product Catalogue Download", message: `Catalogue download request — ${form.company || form.name}, ${form.country || ""}` });
      track("catalogue_lead_submit");
      setUnlocked(true);
    } catch (err) {
      /* even if storage fails, allow download */
      setUnlocked(true);
    } finally {
      setBusy(false);
    }
  };

  const download = () => {
    track("catalogue_download");
    setDownloaded(true);
    if (catalogueUrl) window.open(catalogueUrl, "_blank", "noopener");
  };

  return (
    <>
      <Seo
        title="Product Catalogue — Indian Food & Agricultural Products | Orvella Overseas"
        description="Download the Orvella Overseas product catalogue — rice, spices, makhana, dehydrated fruits and vegetables, peanuts, tea and food powders from India for international B2B buyers."
        path="/catalogue"
      />

      <PageHero
        eyebrow="Product Catalogue"
        title="Download Our Product Catalogue"
        sub="A complete overview of our product portfolio, categories and sourcing approach — prepared for international B2B buyers."
      />

      <section className="section-pad">
        <div className="container-x grid items-start gap-10 lg:grid-cols-2">
          <Reveal>
            <span className="eyebrow">What's Inside</span>
            <span className="mt-3 block h-[2px] w-12 bg-gold" />
            <h2 className="mt-5 font-serif text-3xl font-semibold text-navy">Our Portfolio, At a Glance</h2>
            <TickList
              className="mt-6"
              items={(categories || []).map((c) => `${c.name} — ${c.tagline || c.description}`.slice(0, 90))}
            />
            {downloaded && (
              <p className="mt-6 inline-flex items-center gap-2 rounded-lg bg-gold/10 px-4 py-3 text-sm font-semibold text-gold-dark" data-testid="catalogue-download-started">
                <CheckCircle2 size={16} /> Your download has started. A copy of the catalogue link stays in settings if you need it again.
              </p>
            )}
          </Reveal>

          <Reveal delay={0.1}>
            <div className="card p-6 sm:p-8" data-testid="catalogue-download-card">
              {!catalogueUrl ? (
                <>
                  <h3 className="font-serif text-xl font-semibold text-navy">Catalogue Coming Soon</h3>
                  <p className="mt-2 text-sm leading-relaxed text-mist">
                    The downloadable catalogue is being prepared and will be published here shortly. [TO BE UPDATED]
                  </p>
                  <p className="mt-4 text-sm leading-relaxed text-mist">
                    In the meantime, our export team can share product details and specifications directly — request a quote or
                    contact us on WhatsApp.
                  </p>
                </>
              ) : needsInfo && !unlocked ? (
                <form onSubmit={submit} data-testid="catalogue-lead-form">
                  <h3 className="font-serif text-xl font-semibold text-navy">Get the Catalogue</h3>
                  <p className="mt-1.5 text-sm text-mist">Tell us a little about you — then download instantly.</p>
                  <div className="mt-5 grid gap-4">
                    <div>
                      <label className="flabel">Name <span className="text-gold-dark">*</span></label>
                      <input className="field" required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="catalogue-name-input" />
                    </div>
                    <div>
                      <label className="flabel">Company</label>
                      <input className="field" value={form.company} onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))} data-testid="catalogue-company-input" />
                    </div>
                    <div>
                      <label className="flabel">Country</label>
                      <input className="field" value={form.country} onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))} data-testid="catalogue-country-input" />
                    </div>
                    <div>
                      <label className="flabel">Business Email <span className="text-gold-dark">*</span></label>
                      <input className="field" type="email" required value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="catalogue-email-input" />
                    </div>
                  </div>
                  <button type="submit" disabled={busy} className="btn-gold mt-6 w-full" data-testid="catalogue-submit-btn">
                    {busy ? <Loader2 size={16} className="animate-spin" /> : <FileDown size={16} />}
                    {busy ? "Please wait…" : "Continue to Download"}
                  </button>
                </form>
              ) : (
                <div className="text-center">
                  <h3 className="font-serif text-xl font-semibold text-navy">Ready to Download</h3>
                  <p className="mt-1.5 text-sm text-mist">Orvella Overseas — Product Catalogue (PDF)</p>
                  <button onClick={download} className="btn-gold mt-5 w-full" data-testid="catalogue-download-btn">
                    <FileDown size={16} /> Download Product Catalogue
                  </button>
                </div>
              )}
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
